import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';
import * as _ from 'lodash';
import { useDispatch } from 'react-redux';
import {
  API,
  capitalizeFirstLetterEachWord,
  CertificateStatus,
  getEditPermission,
  getUuidV4,
  getViewPermission,
  Logging,
  PermissionGroup,
  SelectorButton,
  SelectorButtonOption,
  UserContext,
  UserInfo,
  Notification,
  Certificate,
  CertificateSummary,
  NotificationData,
  ResponseError,
  SettingsContextObject,
  getCreatePermission,
  BusyDialog,
} from '@vcd-ecm-ui/Common';
import {
  GenericGrid,
  ActionMenu,
  ColumnLink,
  FilterModelApi,
  GridDataSummaryCollection,
  SortModelApi,
  Action,
  ActionDivider,
  mergeDefaultAndCustomColumns,
  getSelectFilterAsString,
  FilterTypeEnum,
  GridFilter,
  GridSort,
  SelectFilterType,
  getSelectFilterFromFilters,
  BulkAction,
  BulkActionsInfo,
} from '@vcd-ecm-ui/GenericGrid';
import { CertificateRejectionViewer, SideBySideViewer } from '@vcd-ecm-ui/SideBySideViewer';
import { VtxButton, VtxColumnProps, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import CertificateGridProps from './CertificateGridProps.interface';
import GridRowData, { GridDataNativeType } from './GridRowData';
import getDefaultColumns, { additionalTableSettings } from './defaultColumns';
import getDefaultColumnsCompact, { additionalTableSettingsCompact } from './defaultColumnsCompact';
import './common-reports.scss';
import './certificate-grid.scss';
import useJurisdictions from './useJurisdictions';

const CertificateGrid = ({
  gatewayEndPoint,
  displayGridOnly = false,
  customerUUID = null,
  parentCustomerUUID = null,
  onCertificateApproved = null,
  onCertificateEdited = null,
}: CertificateGridProps): JSX.Element => {
  // Get the User Context
  const userContext: UserInfo = React.useContext(UserContext);
  const { ccPreventEcmEdits } = useFlags();

  const hasBulkUploadViewPermission: boolean = getViewPermission(userContext, PermissionGroup.BulkUpload);
  const hasCertificatesCreatePermission: boolean = getCreatePermission(userContext, PermissionGroup.Certificates);
  const hasCertificatesViewPermission: boolean = getViewPermission(userContext, PermissionGroup.Certificates);
  const hasCertificatesEditPermission: boolean = getEditPermission(userContext, PermissionGroup.Certificates);

  const { settingsContext, setSettingsContext } = React.useContext(SettingsContextObject);
  const [notification, setNotification] = React.useState(null);
  const [manualRefreshKey, setManualRefreshKey] = React.useState(null);
  const [openSideBySideViewer, setOpenSideBySideViewer] = React.useState(false);
  const [selectedCertificate, setSelectedCertificate] = React.useState(null);
  const [certificateRejectionViewerOpen, setCertificateRejectionViewerOpen] = React.useState(false);
  const [operationPending, setOperationPending] = React.useState(null);
  const [jurisdictions, jurisdictionNotification] = useJurisdictions(gatewayEndPoint);

  const [numberPendingRowsSelected, setNumberPendingRowsSelected] = React.useState(0);

  const dispatch = useDispatch();

  // Get the Status Filter
  const getStatusFilter = (status: string): GridFilter => {
    const statusFilter: GridFilter = {
      column: 'status',
      value: status === CertificateStatus.All
        ? null
        : getSelectFilterAsString(FilterTypeEnum.Text, null, false, [status], [capitalizeFirstLetterEachWord(status)]),
    };

    return statusFilter;
  };

  // Selected Status
  const [selectedStatus, setSelectedStatus] = React.useState(sessionStorage.certificateStatus
    ? sessionStorage.certificateStatus
    : CertificateStatus.Pending);

  // Create teh External Status Filter
  const [externalFilter, setExternalFilter] = React.useState(getStatusFilter(selectedStatus));

  // Parse each row from the Native Data Type to the Actual Data Type
  const rowParser = (row: GridDataNativeType): GridRowData => GridRowData.fromData(row);

  // Certificate Id Click
  const onCertificateIdClick = (row: GridRowData): void => {
    Logging.Info('Certificate Id Clicked:', row.rowData.certificateId, row.rowData.uuid);

    setSelectedCertificate(row.rowData);

    // Open the Side By Side Viewer
    setOpenSideBySideViewer(true);
  };

  // The Certificate Grid uses V4 CertificatesSummaries, while SBS Viewer uses V4 Certificates.
  // Transform from CertificateSummary to Certificate
  const transformCertificatesSummarytoV4 = (
    certificateSummaries: CertificateSummary | CertificateSummary[],
  ): Certificate | Certificate[] => {
    if (Array.isArray(certificateSummaries)) {
      const v4Certificates: Certificate[] = [];

      certificateSummaries.forEach((certificateSummary: CertificateSummary) => {
        // Only the Id and Uuid is needed
        v4Certificates.push({ exemptionId: certificateSummary.certificateId, uuid: certificateSummary.uuid });
      });

      return v4Certificates;
    }

    // Only the Id and Uuid is needed
    const v4Certificate: Certificate = { exemptionId: certificateSummaries.certificateId, uuid: certificateSummaries.uuid };

    return v4Certificate;
  };

  // Update the Status of Certificates
  const updateCertificatesStatus = (status: CertificateStatus, certificates: Certificate | Certificate[]) => {
    // Trigger a Reload of the Data
    setManualRefreshKey(getUuidV4());
  };

  // Certificate(s) were Approved : Notify the User
  const onApproved = (
    successCertificates: Certificate | Certificate[],
    failureCertificates: Certificate | Certificate[],
    numberConflictErrors: number, // 409 Conflict Exceptions
  ): void => {
    if (Array.isArray(successCertificates) && Array.isArray(failureCertificates)) {
      Logging.Info('Certificates Approved:', successCertificates.length, failureCertificates.length, numberConflictErrors);

      const description: string[] = [];

      if (successCertificates.length) {
        const successDescription = successCertificates.length > 1
          ? `${successCertificates.length} Certificates were Approved. `
          : `${successCertificates.length} Certificate was Approved. `;

        description.push(successDescription);
      }

      if (failureCertificates.length) {
        const failureDescription = failureCertificates.length > 1
          ? `${failureCertificates.length} Certificates failed to Approve. `
          : `${failureCertificates.length} Certificate failed to Approved. `;

        description.push(failureDescription);

        // All Certificates failed due to 409 Conflicts.  Add a Special message
        if ((!successCertificates.length) && (numberConflictErrors === failureCertificates.length)) {
          const conflictDescription = 'Certificate approval cannot be completed until your custom domain configuration is verified.';
          description.push(conflictDescription);
        }
      }

      const notificationData: NotificationData = {
        // Use Information, Warning and Error icons based on the Results
        type: failureCertificates.length ? successCertificates.length ? 'Warning' : 'Error' : 'Information',
        autoCloseTimeOutMilliseconds: 2000,
        hideProgress: true,
        title: 'Certificate Approval Status',
        description,
        position: 'BottomRight',
      };

      setNotification(notificationData);
    } else {
      const certificate: Certificate = (successCertificates as Certificate);
      Logging.Info('Certificate Approved:', certificate.uuid, certificate.exemptionId);

      const notificationData: NotificationData = {
        type: 'Information',
        autoCloseTimeOutMilliseconds: 2000,
        hideProgress: true,
        title: 'Certificate Approval Status',
        description: `Certificate ${certificate.exemptionId} was Approved`,
        position: 'BottomRight',
      };

      setNotification(notificationData);
    }

    // Inform anyone that is interested in knowing
    if (onCertificateApproved) {
      onCertificateApproved();
    }

    // Update the Certificate Grid to reflect the current Certificate Status
    updateCertificatesStatus(CertificateStatus.Approved, successCertificates);
  };

  // Certificate was Rejected : Notify the User
  const onRejected = (certificate: Certificate): void => {
    // Close the Rejection Viewer
    setCertificateRejectionViewerOpen(false);

    Logging.Info('Certificate Rejected:', certificate.uuid, certificate.exemptionId);

    const notificationData: NotificationData = {
      type: 'Information',
      autoCloseTimeOutMilliseconds: 2000,
      hideProgress: true,
      title: 'Certificate Approval Status',
      description: `Certificate ${certificate.exemptionId} was Rejected`,
      position: 'BottomRight',
    };

    setNotification(notificationData);

    // Update the Certificate Grid to reflect the current Certificate Status
    updateCertificatesStatus(CertificateStatus.Rejected, certificate);
  };

  // Make the API Calls to Approve the Certificate(s)
  const approveCertificates = (
    certificates: CertificateSummary | CertificateSummary[],
    clearSelection: () => void,
  ) => async (): Promise<void> => {
    let certificateId: number = null;

    try {
      const apiInterface = new API(gatewayEndPoint);

      if (Array.isArray(certificates)) {
        const promises: Promise<any>[] = []; // eslint-disable-line @typescript-eslint/no-explicit-any

        // Create an Array of Promises for execution in parallel.
        for (let i = 0; i < certificates.length; i += 1) {
          const certificate: CertificateSummary = certificates[i];
          certificateId = certificate.certificateId;

          Logging.Debug('Approve', certificate.uuid, certificate.certificateId);

          promises.push(apiInterface.certificate.approveUserCertificateV41(certificate.uuid));
        }

        Logging.Info('Approving Multiple Certificates', promises.length);

        // Catch the API Error for each Promise and return it as part of the Overall Result
        const handlePromiseRejection = (promise: Promise<any>) => { // eslint-disable-line @typescript-eslint/no-explicit-any, arrow-body-style
          return promise.catch((error: ResponseError) => { // eslint-disable-line arrow-body-style
            return error;
          });
        };

        // Start execution on all the Promises.  Assign an Error Handler to each Promise. Want to catch the errors separately.
        Promise.all(promises.map(handlePromiseRejection))
          // Wait for all the Promises to Execute
          .then((results: any[] | ResponseError[]) => { // eslint-disable-line @typescript-eslint/no-explicit-any
            const successCertificates: CertificateSummary[] = [];
            const failureCertificates: CertificateSummary[] = [];
            let numberConflictErrors = 0;

            // Split the Success and Failure Certificates
            results.forEach((result: any | ResponseError, index: number) => { // eslint-disable-line @typescript-eslint/no-explicit-any
              // Check for Success or Failure
              if (result) {
                const responseError: ResponseError = result as ResponseError;

                // Count the 409 Conflicts
                if (responseError.statusCode === 409) {
                  numberConflictErrors += 1;
                }

                Logging.Info('Approve Error', result);
                failureCertificates.push(certificates[index]);
              } else {
                successCertificates.push(certificates[index]);
              }
            });

            // Clear the Grid Selection in Bulk Action
            if (clearSelection) {
              clearSelection();
            }

            // Bulk Approve Operation is Done
            setOperationPending(null);

            // Notify the User
            onApproved(
              transformCertificatesSummarytoV4(successCertificates),
              transformCertificatesSummarytoV4(failureCertificates),
              numberConflictErrors,
            );
          });
      } else {
        certificateId = certificates.certificateId;

        Logging.Info('Approve', certificates.uuid, certificates.certificateId);

        await apiInterface.certificate.approveUserCertificateV41(certificates.uuid);

        // Notify the User
        onApproved(transformCertificatesSummarytoV4(certificates), null, 0);
      }
    } catch (error) {
      const responseError: ResponseError = error as ResponseError;

      // Handle Single Certificate Approve Error
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Certificate Approval Error',
        // Append error description in case of 409 Conflict
        description: `Unable to approve Certificate '${certificateId}'.`,
        error: error as ResponseError,
        appendMessagesForStatusCodes: [409],
        hideStatusCodes: [409],
      };

      setNotification(notificationData);
    }
  };

  // Approve Menu clicked in Actions
  const onApprove = (certificate: CertificateSummary): void => {
    if (!certificate) {
      Logging.Warning('Approve: Unable to get Certificate');
    } else {
      // Dispatch the Action the Approve the Certificate
      dispatch(approveCertificates(certificate, null));
    }
  };

  // Reject Menu clicked in Actions
  const onReject = (certificate: CertificateSummary): void => {
    if (!certificate) {
      Logging.Warning('Reject: Unable to get Certificate');
    } else {
      Logging.Info('Reject', certificate.uuid, certificate.certificateId);

      // Save the Selected Certificate (Id and Uuid)
      setSelectedCertificate({ uuid: certificate.uuid, id: certificate.certificateId });

      // Open the Certificate Rejection Viewer
      setCertificateRejectionViewerOpen(true);
    }
  };

  // Cancel the Rejection Viewer
  const onCancelRejectionViewer = () => {
    // Close the Rejection Viewer
    setCertificateRejectionViewerOpen(false);
  };

  // Generic Action Menu Handler
  const onActionMenuClick = (actionKey: string, row: GridRowData): void => {
    const certificateUuid: string = row.rowData.uuid; // eslint-disable-line prefer-destructuring

    Logging.Info('Action Menu Clicked:', actionKey, certificateUuid, row.rowData);

    switch (actionKey) {
      case 'view':
        Logging.Info('View Certificate', certificateUuid);
        setSelectedCertificate(row.rowData);
        setOpenSideBySideViewer(true);
        break;
      case 'approve':
        onApprove(row.rowData);
        break;
      case 'reject':
        onReject(row.rowData);
        break;
      default:
        break;
    }
  };

  // Get the Action Menu Actions for the Row
  const getActionMenuActions = (row: GridRowData): (Action | ActionDivider)[] => {
    const isPending = row.status === CertificateStatus.Pending;

    const actions: (Action | ActionDivider)[] = [];

    if (hasCertificatesViewPermission) {
      actions.push(
        {
          key: 'view',
          name: 'View',
          onClick: onActionMenuClick,
          row,
        },
      );
    }

    if (hasCertificatesEditPermission) {
      /*
      actions.push(new ActionDivider());
      actions.push(
        {
          key: 'edit',
          name: 'Edit',
          onClick: onActionMenuClick,
          row,
          disabled: (ccPreventEcmEdits === true),
        },
      );
      */
      actions.push(new ActionDivider());
      actions.push(
        {
          key: 'approve',
          name: 'Approve',
          onClick: onActionMenuClick,
          row,
          disabled: !isPending || (ccPreventEcmEdits === true),
        },
      );
      actions.push(new ActionDivider());
      actions.push(
        {
          key: 'reject',
          name: 'Reject',
          onClick: onActionMenuClick,
          row,
          disabled: !isPending || (ccPreventEcmEdits === true),
        },
      );
    }

    return actions;
  };

  // Get the Columns for the Grid
  const getColumns = (): VtxColumnProps<GridRowData>[] => {
    // Get the Default Columns
    const defaultColumns: VtxColumnProps<GridRowData>[] = displayGridOnly ? getDefaultColumnsCompact(settingsContext.certificates) : getDefaultColumns(settingsContext.certificates); // eslint-disable-line max-len

    // Column Columns with Dynamic Renderer
    const customColumns = [
      {
        dataIndex: 'jurisdiction',
        filters: jurisdictions?.map((jurisdiction) => { // eslint-disable-line arrow-body-style
          return {
            text: jurisdiction.name,
            value: jurisdiction.abbreviation,
          };
        }),
      },
      {
        dataIndex: 'action',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (value: any, row: GridRowData, index: number) => <ActionMenu actions={getActionMenuActions(row)} />,
      },
      {
        dataIndex: 'certificateId',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (value: any, row: GridRowData, index: number) => (
          <ColumnLink
            id="certificateIdLink"
            icon={row.additionalDocumentation ? 'paper-clip' : null}
            iconPlacement="right"
            linkText={value}
            onClick={onCertificateIdClick}
            row={row}
          />
        ),
      },
    ];

    // Column Columns with Dynamic Renderer
    const customColumnsCompact = [
      {
        dataIndex: 'jurisdiction',
        filters: jurisdictions?.map((jurisdiction) => { // eslint-disable-line arrow-body-style
          return {
            text: jurisdiction.name,
            value: jurisdiction.abbreviation,
          };
        }),
      },
      {
        dataIndex: 'certificateId',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (value: any, row: GridRowData, index: number) => (
          <ColumnLink
            id="certificateIdLink"
            icon={row.additionalDocumentation ? 'paper-clip' : null}
            iconPlacement="right"
            linkText={value}
            onClick={onCertificateIdClick}
            row={row}
          />
        ),
      },
    ];

    // Merge the Custom Columns into the Default Columns
    return mergeDefaultAndCustomColumns(defaultColumns, displayGridOnly ? customColumnsCompact : customColumns);
  };

  // The API Call used to retrieve data for the Grid
  const apiGetRowsServerModel = (
    startRow:number,
    endRow:number,
    filterModel: FilterModelApi,
    sortModel: SortModelApi,
  ): Promise<GridDataSummaryCollection> => {
    const api = new API(gatewayEndPoint);

    let filterModelCopy: FilterModelApi = filterModel;

    if (displayGridOnly) {
      // Create a Custom Filter using the Customer uuid and Parent Customer uuid
      const additionalCustomerUuuidFilter = parentCustomerUUID
        ? `(customeruuid eq '${customerUUID}' or customeruuid eq '${parentCustomerUUID}')`
        : `(customeruuid eq '${customerUUID}')`;

      if (filterModelCopy === null) {
        filterModelCopy = [];
      }

      filterModelCopy.push(additionalCustomerUuuidFilter);
    }

    return api.certificate.getCertificatesV41(startRow, endRow, filterModelCopy, sortModel);
  };

  // Status Selection Changed
  const onSelectStatus = (status: string): void => {
    setSelectedStatus(status);

    // Save the Status to the Session Storage
    sessionStorage.certificateStatus = status;

    // Update the External Filter
    setExternalFilter(getStatusFilter(status));
  };

  // Filtering or Sorting in the Grid Changed
  const onFilterSortingChange = (filters: GridFilter[], sorters: GridSort[]): void => {
    Logging.Debug('onFilterSortingChange', filters, sorters);

    // Get the Status Filter
    const selectFilter: SelectFilterType = getSelectFilterFromFilters(filters, 'status');
    const status = selectFilter ? selectFilter.values[0] : CertificateStatus.All;

    // Update the Status Button
    setSelectedStatus(status);

    // Save the Status to the Session Storage
    sessionStorage.certificateStatus = status;

    // Update the External Filter
    setExternalFilter(getStatusFilter(status));
  };

  // Certificate Approved in SBS Viewer
  const onSideBySideViewerApproved = (certificate: Certificate): void => {
    setOpenSideBySideViewer(false);

    onApproved(certificate, null, 0);
  };

  // Certificate Rejected in SBS Viewer
  const onSideBySideViewerRejected = (certificate: Certificate): void => {
    setOpenSideBySideViewer(false);

    onRejected(certificate);
  };

  // Certificate Edited in SBS Viewer
  const onSideBySideViewerEdited = (certificate: Certificate): void => {
    // Refresh the Grid
    setManualRefreshKey(getUuidV4());

    // Inform anyone that is interested in knowing
    if (onCertificateEdited) {
      onCertificateEdited(certificate);
    }
  };

  // Close the Side By Side Viewer
  const onSideBySideViewerClose = () => {
    // Close the Side By Size Viewer
    setOpenSideBySideViewer(false);
  };

  // Close the Notification
  const onNotificationClose = (): void => {
    // Clear the Certificates Notification
    setNotification(null);
  };

  // Row Selection has Changed
  const onSelectedRowsChange = (rowKeysSelected: React.Key[], rowsSelected: GridRowData[]): void => {
    let pendingRowsSelected = 0;

    // count the Number of Pending Rows Selected
    rowsSelected.forEach((row: GridRowData) => {
      if (row.status === CertificateStatus.Pending) {
        pendingRowsSelected += 1;
      }
    });

    Logging.Info('onSelectedRowsChange:', pendingRowsSelected, rowsSelected.length);

    setNumberPendingRowsSelected(pendingRowsSelected);
  };

  // Bulk Action Handler
  const onApproveSelected = (selectedRowKeys: string[], selectedRows: GridRowData[], clearSelection: () => void): void => {
    Logging.Info('Approve Selected', selectedRowKeys, selectedRows);

    // Rows were selected
    if (selectedRows.length) {
      const certificates: CertificateSummary[] = [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      selectedRows.forEach((row: GridRowData) => {
        const certificate: CertificateSummary = row.rowData;

        // Only add Certificates in Pending State
        if (row && row.status === CertificateStatus.Pending) {
          certificates.push(certificate);
        }
      });

      if (certificates.length) {
        Logging.Info('onApproveSelected', selectedRows.length);

        const operationMessage = `Busy Approving ${selectedRows.length} certificate${selectedRows.length === 1 ? '' : 's'}`;

        // Bulk Approve Operation is Pending
        setOperationPending(operationMessage);

        // Dispatch the Action to Approve the Certificates
        dispatch(approveCertificates(certificates, clearSelection));
      } else {
        // Clear the Grid Selection
        clearSelection();
      }
    }
  };

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Certificate Grid: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
    };
  }, []);

  const certStatusOptions: SelectorButtonOption[] = [
    {
      label: CertificateStatus.Pending,
      value: CertificateStatus.Pending,
      bulletClass: 'bullet-pending',
      statusClass: 'status-pending',
      userSettable: true,
    },
    {
      label: CertificateStatus.Approved,
      value: CertificateStatus.Approved,
      bulletClass: 'bullet-approved',
      statusClass: 'status-approved',
      userSettable: true,
    },
    {
      label: CertificateStatus.Rejected,
      value: CertificateStatus.Rejected,
      bulletClass: 'bullet-rejected',
      statusClass: 'status-rejected',
      userSettable: true,
    },
    {
      label: CertificateStatus.Draft,
      value: CertificateStatus.Draft,
      bulletClass: 'bullet-draft',
      statusClass: 'status-draft',
      userSettable: true,
    },
    {
      label: CertificateStatus.All,
      value: CertificateStatus.All,
      bulletClass: 'bullet-all',
      statusClass: 'status-all',
      userSettable: true,
    },
  ];

  // Bulk Actions
  const bulkActions: BulkActionsInfo = {
    title: 'Bulk Actions',
    type: 'primary',
    actions: [
      {
        id: 'approveSelected',
        name: numberPendingRowsSelected ? `Approve Selected (${numberPendingRowsSelected})` : 'Approve Selected',
        disabled: (numberPendingRowsSelected === 0),
        onClick: onApproveSelected,
      },
    ],
  };

  // Check if Helper Data is Loaded before Showing the Grid
  const dataLoaded = jurisdictions;

  const showAddCertificate = false;

  return (
    <>
      <div className={displayGridOnly ? 'reportsContainerCompact' : 'reportsContainer'}>
        {
          !displayGridOnly && (
            <div className="reportsHeader">
              <div className="reportsHeaderLeft">
                Certificates
                <SelectorButton
                  name="CertificateStatus"
                  onSelect={onSelectStatus}
                  options={certStatusOptions}
                  selected={selectedStatus}
                />
              </div>
              <div className="reportsHeaderRight">
                {
                  hasCertificatesCreatePermission && showAddCertificate && (
                    <a href="#/certificate/add" title="Add Certificate" className="reportsHeaderButton">
                      <VtxButton
                        id="addCertificateButton"
                        icon={<VtxIcon type="plus" />}
                        type="default"
                      >
                        Add Certificate
                      </VtxButton>
                    </a>
                  )
                }
                {
                  hasBulkUploadViewPermission && (
                    <a href="#/bulkupload" title="Bulk Upload" className="reportsHeaderButton">
                      <VtxButton
                        id="bulkUploadButton"
                        icon={<VtxIcon type="upload1" />}
                        type="default"
                      >
                        Bulk Upload
                      </VtxButton>
                    </a>
                  )
                }
              </div>
            </div>
          )
        }
        {/* Change the Key in the Parent Div to Reload */}
        <div className={displayGridOnly ? 'reportsContentGridCompact' : 'reportsContentGrid'}>
          {
            dataLoaded && (
              <GenericGrid<GridRowData>
                id={displayGridOnly ? 'certificateGridCompact' : 'certificateGrid'}
                dataTypeDescription={displayGridOnly ? 'Exemptions' : 'Certificates'}
                getDefaultColumns={getColumns}
                rowParser={rowParser}
                apiGetRowsServerModel={apiGetRowsServerModel}
                // This will handle double header line columns. Single: 30px, Double: 50px
                verticalHeightOffset={displayGridOnly ? (parentCustomerUUID ? 446 : 415) : 339}
                model="server"
                additionalTableSettings={displayGridOnly ? additionalTableSettingsCompact : additionalTableSettings}
                onFilterSortingChange={onFilterSortingChange}
                externalFilter={displayGridOnly ? null : externalFilter}
                alwaysHiddenFilterBreadcrumbs={displayGridOnly ? [] : ['status']}
                bulkActions={hasCertificatesEditPermission && !displayGridOnly && false ? bulkActions : null} // Disable Bulk Actions temporarily
                refreshKey={manualRefreshKey}
                showTableManagement={!displayGridOnly}
                showAllFavoriteColumns={!displayGridOnly}
                showClearFilters={!displayGridOnly}
                showClearSorters={!displayGridOnly}
                showLoadingIndicator
                onSelectedRowsChange={onSelectedRowsChange}
              />
            )
          }
        </div>
        <Notification
          data={notification}
          onNotificationClose={onNotificationClose}
          autoClose
        />
        <SideBySideViewer
          isOpen={openSideBySideViewer}
          onClose={onSideBySideViewerClose}
          onApproved={onSideBySideViewerApproved}
          onRejected={onSideBySideViewerRejected}
          onEdited={onSideBySideViewerEdited}
          certificateId={selectedCertificate?.id}
          certificateUuid={selectedCertificate?.uuid}
          gatewayEndPoint={gatewayEndPoint}
        />
        {
          operationPending && (
            <BusyDialog
              busyMessage={operationPending}
              isOpen={operationPending !== null}
            />
          )
        }
        {
          certificateRejectionViewerOpen && (
            <CertificateRejectionViewer
              gatewayEndPoint={gatewayEndPoint}
              isOpen={certificateRejectionViewerOpen}
              certificateUuid={selectedCertificate?.uuid}
              certificateId={selectedCertificate?.id}
              certificate={null}
              onRejected={onRejected}
              onCancel={onCancelRejectionViewer}
            />
          )
        }
      </div>
    </>
  );
};

export default CertificateGrid;
